import { Loader2 } from "lucide-react";
import { memo, useEffect, useState } from "react";
import {
	type OptimisticEntry,
	useOptionalEntries,
} from "@/shared/contexts/EntriesContext";
import { AppIcon } from "@/v2/lib/appIcons";

/**
 * Optimistic "processing" rows shown at the top of the entries list the moment
 * the backend accepts an activity chunk (the `entry received` socket event),
 * before the real entry exists. They fill in progressively (matter, then the
 * finished row) so generation feels fast — see .docs/loading-improvement.md.
 *
 * Rendered as a non-virtualized band above the table so it doesn't fight the
 * virtualizer. Only `create` entries render here; `update` entries just drive
 * the processing indicator, since their real row already exists.
 */

// Sayings rotated within each backend phase (~3s apart) to give a sense of
// progress. Pools are phase-scoped, so rotating never implies a cross-phase step
// was skipped (the bug the old linear stage list had), and the narrative pool
// mirrors the real backend sub-steps (summary → draft → refine). Holds on the
// last saying once the pool is exhausted, so long generations don't loop.
const PHASE_MESSAGES: Record<OptimisticEntry["phase"], readonly string[]> = {
	received: ["Reading the activity", "Predicting the matter"],
	matter: [
		"Summarizing the source",
		"Drafting the narrative",
		"Refining the wording",
		"Wrapping up the narrative",
	],
	finalizing: ["Finalizing"],
	error: ["Generation failed"],
};

const MESSAGE_INTERVAL_MS = 3000;

// Screen-reader announcement per phase — the meaningful step, NOT the cosmetic
// sub-sayings. Announced once per phase (via role="status") so assistive tech
// isn't nagged by the 3s rotation.
const PHASE_ANNOUNCEMENT: Record<OptimisticEntry["phase"], string> = {
	received: "Predicting the matter",
	matter: "Generating narrative",
	finalizing: "Finalizing entry",
	error: "Entry generation failed",
};

// Rotates through the current phase's pool, restarting when the phase changes
// and holding on the last saying once the pool runs out.
function useCyclingMessage(phase: OptimisticEntry["phase"]): string {
	const pool = PHASE_MESSAGES[phase];
	const [index, setIndex] = useState(0);
	useEffect(() => {
		setIndex(0);
		const id = setInterval(() => {
			setIndex((n) => Math.min(n + 1, pool.length - 1));
		}, MESSAGE_INTERVAL_MS);
		return () => clearInterval(id);
	}, [pool]);
	return pool[Math.min(index, pool.length - 1)];
}

function ProcessingRow({ entry }: { entry: OptimisticEntry }) {
	const isError = entry.phase === "error";
	const message = useCyclingMessage(entry.phase);
	// Show the predicted matter alongside the narrative-generation messages.
	const detail =
		entry.phase === "matter" && entry.matter ? ` · ${entry.matter}` : "";
	return (
		<div className="flex items-center gap-3 border-b border-border/60 bg-muted/30 px-3 py-2 animate-in fade-in-0 slide-in-from-top-1">
			<span className="flex size-6 shrink-0 items-center justify-center text-muted-foreground">
				{entry.application ? (
					<AppIcon application={entry.application} className="size-5" />
				) : (
					<Loader2 className="size-4 animate-spin" />
				)}
			</span>

			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium">
					{entry.documentName || "New time entry"}
				</p>
				{/* Visible text cycles cosmetically every ~3s — hide it from screen
					readers and announce only the phase below. */}
				<p className="truncate text-xs text-muted-foreground" aria-hidden>
					{isError ? (
						<span className="text-destructive">{message}</span>
					) : (
						<span className="inline-flex items-center gap-1.5">
							<Loader2 className="size-3 animate-spin" />
							{message}
							{detail}…
						</span>
					)}
				</p>
				{/* <output> has an implicit role="status" + aria-live="polite". */}
				<output className="sr-only">{PHASE_ANNOUNCEMENT[entry.phase]}</output>
			</div>

			{/* Narrative placeholder shimmer */}
			<div className="hidden h-3 w-40 animate-pulse rounded bg-muted sm:block" />
		</div>
	);
}

export const EntryProcessingRows = memo(function EntryProcessingRows() {
	// No-op on surfaces (admin/internal) that render EntriesTable without an
	// EntriesProvider — they have no processing socket, so nothing to show.
	const entries = useOptionalEntries();
	const rows = (entries?.optimisticEntries ?? []).filter(
		(entry) => entry.mode === "create",
	);
	if (rows.length === 0) return null;
	return (
		<div className="shrink-0">
			{rows.map((entry) => (
				<ProcessingRow key={entry.entryId} entry={entry} />
			))}
		</div>
	);
});
